import { tokens } from "../../../theme";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { closeButtonStyles } from "../../../styles/buttonStyles";
import ActionButton from "./ActionButton";
import ActiveLabelRow from "./ActiveLabelRow";
import LabelGroup from "./LabelGroup";
import useIamModal from "../../IamModal/UseIamModal";
import useResizableDrawerWidth from "../../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../../components/DrawerSnapToggle";
import { releaseStyles } from "../styles";
import {
  actionText,
  formatLabelDate,
  groupReleaseLabels,
  pickReleaseDrawerAction,
} from "../summaryHelpers";
import { getHeaders } from "../../../utils/apiclient";

const TERMINAL_GROUPS = [
  { key: "inProduction", title: "In production" },
  { key: "failed", title: "Failed validation" },
  { key: "superseded", title: "Superseded" },
  { key: "discarded", title: "Discarded" },
];

// Every label of one release, grouped by state. Data comes from the row that was clicked.
const ReleaseLabelsDrawer = ({
  release,
  app,
  onClose,
  showReleaseStatusDialog,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const { GROUP_DOT_COLORS, labelsDrawer, statusChip } = releaseStyles(colors);

  const releaseLabels = release?.releaseLabels || [];
  const groups = groupReleaseLabels(releaseLabels);
  const releaseActions = release?.actionsAllowed || [];
  const releaseAction = pickReleaseDrawerAction(releaseActions);
  const releasePath = `/apps/${app}/releases/${release?.name}`;
  // Discarding a whole release is its own header action, next to Archive —
  // the per-label Discard in ActiveLabelRow only drops a single label.
  const canDiscardRelease = releaseActions.includes("DISCARD");

  // The drawer's own width object switches at "sm", not "md" like the other
  // drawers, so the resize handle gates on the same breakpoint it renders at.
  const isWideEnough = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const {
    width: drawerWidth,
    isWide: isDrawerWide,
    toggleWide: toggleDrawerWide,
    handleProps: resizeHandleProps,
  } = useResizableDrawerWidth({
    storageKey: "release-labels",
    defaultWidth: 560,
    maxWidth: 900,
    enabled: isWideEnough,
  });

  // IAM scoped to this one release. The page header's IAM button covers
  // /releases as a whole; this one covers /releases/{name}. The release name
  // is a dependency because useHasPermission only re-checks when deps change.
  const { IamModal, IamButton, canViewIamModal } = useIamModal(
    releasePath,
    [release?.name],
    labelsDrawer.iamButton
  );

  const runReleaseAction = (action) =>
    showReleaseStatusDialog(
      `Perform action ${action} on release ${release?.name}`,
      `/${release?.name}`,
      {
        headers: { ...getHeaders(), Action: action },
      }
    );

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      keepMounted={false}
      open={Boolean(release)}
      onClose={onClose}
      PaperProps={{
        sx: { ...labelsDrawer.paper(drawerWidth), overflow: "visible" },
      }}
    >
      <DrawerResizeHandle
        {...resizeHandleProps}
        ariaLabel="Resize release labels drawer"
      />
      <DrawerSnapToggle
        enabled={isWideEnough}
        isWide={isDrawerWide}
        onClick={toggleDrawerWide}
        ariaLabel="Expand release labels drawer to 75% width"
        collapseLabel="Collapse release labels drawer"
        sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
      />
      {release ? (
        <>
          <Box sx={labelsDrawer.header}>
            <Box sx={labelsDrawer.headerText}>
              <Box sx={labelsDrawer.titleRow}>
                <Typography sx={labelsDrawer.title}>{release?.name}</Typography>
                <Box component="span" sx={statusChip(release?.status)}>
                  {release?.status}
                </Box>
              </Box>
              <Typography sx={labelsDrawer.subtitle}>
                {`${
                  releaseLabels.length
                } labels \u00b7 created ${formatLabelDate(
                  release?.dateCreated
                )}`}
              </Typography>
            </Box>
            <Box sx={labelsDrawer.headerActions}>
              {releaseAction ? (
                <ActionButton
                  action={releaseAction}
                  path={releasePath}
                  text={actionText(releaseAction, release?.status)}
                  sx={labelsDrawer.rowButton}
                  onClick={() => runReleaseAction(releaseAction)}
                />
              ) : null}
              {canDiscardRelease ? (
                <ActionButton
                  action="DISCARD"
                  path={releasePath}
                  text={actionText("DISCARD", release?.status)}
                  sx={labelsDrawer.rowButtonDanger}
                  onClick={() => runReleaseAction("DISCARD")}
                />
              ) : null}
              {canViewIamModal ? (
                <>
                  <Box sx={labelsDrawer.actionDivider} />
                  {IamButton}
                </>
              ) : null}
              <IconButton
                sx={closeButtonStyles(colors)}
                aria-label="Close labels drawer"
                onClick={onClose}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={labelsDrawer.body}>
            <Typography
              sx={labelsDrawer.sectionHeading}
            >{`Active \u00b7 ${groups.active.length}`}</Typography>
            {groups.active.length ? (
              groups.active.map((releaseLabel) => (
                <ActiveLabelRow
                  key={releaseLabel?.name}
                  release={release}
                  releaseLabel={releaseLabel}
                  app={app}
                  showReleaseStatusDialog={showReleaseStatusDialog}
                />
              ))
            ) : (
              <Typography sx={labelsDrawer.emptyText}>
                No active labels
              </Typography>
            )}
            {TERMINAL_GROUPS.map((group) => (
              <LabelGroup
                key={group.key}
                title={group.title}
                dotColor={GROUP_DOT_COLORS[group.key]}
                labels={groups[group.key]}
              />
            ))}
          </Box>
          {IamModal}
        </>
      ) : null}
    </Drawer>
  );
};

export default ReleaseLabelsDrawer;
